import { Button, Callout } from '@dev-spendesk/grapes';
import { Redirect, Route, Switch } from 'react-router';

import { useHostedOnboarding } from 'modules/kyc/hooks/useHostedOnboarding';
import { HeaderWithNavigation } from 'src/core/common/components/HeaderWithNavigation/HeaderWithNavigation';
import { type NavigationLinkProps } from 'src/core/common/components/NavigationLink';
import { ProtectedRoute } from 'src/core/common/components/ProtectedRoute';
import { useTranslation } from 'src/core/common/hooks/useTranslation';
import { routeFor, routes } from 'src/core/constants/routes';
import { useCompany } from 'src/core/modules/app/hooks/useCompany';
import { useUser } from 'src/core/modules/app/hooks/useUser';
import { BillingInformationSection } from 'src/core/modules/company/billing/components/BillingInformationSection';
import { CompanyPlanContainer } from 'src/core/modules/company/billing/components/CompanyPlan';
import { CompanyInformationPage } from 'src/core/modules/company/general-settings/components/CompanyInformationPage';

export const SettingsSpendeskPlanPage = () => {
  const { t } = useTranslation('global');
  const company = useCompany();
  const user = useUser();

  const canUpdateInformationWithHostedOnboarding =
    company.currency === 'GBP' && user.is_account_owner;

  const hostedOnboarding = useHostedOnboarding(window.location.href, {
    isEnabled: canUpdateInformationWithHostedOnboarding,
  });

  const canAccessCompanyInformation = user.is_account_owner || user.is_admin;

  const canAccessBillingPlan = company.churning_at
    ? user.is_account_owner || user.is_controller
    : user.is_account_owner || user.is_admin || user.is_controller;

  const links = [
    canAccessCompanyInformation && {
      key: 'company-information',
      text: t('generalSettings.navLinks.companyInformation'),
      path: routeFor(routes.COMPANY_GENERAL_SETTINGS_COMPANY_INFORMATION.path, {
        company: company.id,
      }),
    },
    canAccessBillingPlan && {
      key: 'billing-plan',
      text: t('billing.navLinks.companyPlan'),
      path: routeFor(routes.COMPANY_BILLING_PLAN.path, {
        company: company.id,
      }),
    },
  ].filter(Boolean) as NavigationLinkProps[];

  const redirectToHostedOnboarding = () => {
    if (hostedOnboarding.status === 'success' && canAccessCompanyInformation) {
      window.location.href = hostedOnboarding.data.url;
    }
  };

  return (
    <div className="page__container">
      <HeaderWithNavigation links={links}>
        {t('navigation.mySpendeskPlan')}
      </HeaderWithNavigation>
      <div className="grow bg-background-primary-default px-40 py-24">
        <div className="mx-auto max-w-[1000px]">
          <Switch>
            <ProtectedRoute
              path={routes.COMPANY_BILLING_PLAN.path}
              isAccessAllowed={canAccessBillingPlan}
            >
              <CompanyPlanContainer />
            </ProtectedRoute>
            <Route
              path={[
                routes.COMPANY_GENERAL_SETTINGS_COMPANY_INFORMATION.path,
                routes.COMPANY_BILLING_INFORMATION.path,
              ]}
            >
              <div className="flex flex-col gap-48">
                {canUpdateInformationWithHostedOnboarding &&
                  hostedOnboarding.status === 'success' && (
                    <div>
                      <Callout
                        title={t(
                          'generalSettings.adyen.completeYourCompanyInformationUpdate',
                        )}
                        variant="info"
                      >
                        <div>
                          <p className="mb-12">
                            {t(
                              'generalSettings.adyen.updateYourDetailsInOurPaymentServicesProvider',
                            )}
                          </p>
                          <Button
                            text={t(
                              'generalSettings.adyen.updateMyInformation',
                            )}
                            onClick={redirectToHostedOnboarding}
                            variant="secondaryNeutral"
                          />
                        </div>
                      </Callout>
                    </div>
                  )}
                {canAccessCompanyInformation && <CompanyInformationPage />}
                {canAccessBillingPlan && <BillingInformationSection />}
              </div>
            </Route>

            {links.length > 0 && <Redirect to={links[0].path} />}

            {links.length === 0 && <Redirect to={routes.HOMEPAGE.path} />}
          </Switch>
        </div>
      </div>
    </div>
  );
};
